import { useCallback, useEffect, useRef, useState } from 'react';
import { saveSection } from '../lib/content';

const clone = (v) => JSON.parse(JSON.stringify(v));
const same = (a, b) => JSON.stringify(a) === JSON.stringify(b);

/**
 * Editable local copy of one content section.
 *
 * `stored` streams in live from Firestore. While the draft is clean we follow
 * it; once the user has edits we stop overwriting them, so a snapshot arriving
 * mid-typing can't wipe the form.
 */
export default function useDraft(section, stored) {
  const [draft, setDraft] = useState(() => clone(stored));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const dirtyRef = useRef(false);

  const dirty = !same(draft, stored);
  dirtyRef.current = dirty;

  useEffect(() => {
    if (!dirtyRef.current) setDraft(clone(stored));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(stored)]);

  const reset = useCallback(() => {
    setDraft(clone(stored));
    setError(null);
  }, [stored]);

  /** Save an explicit value (used by the quick day-entry screen). */
  const saveValue = useCallback(async (value) => {
    setSaving(true);
    setError(null);
    try {
      await saveSection(section, value);
      setDraft(clone(value));
      return true;
    } catch (e) {
      console.error(e);
      setError(e.code === 'permission-denied'
        ? 'Nemaš prava za pisanje — provjeri firestore.rules i svoj UID.'
        : e.message || 'Greška pri spremanju.');
      return false;
    } finally {
      setSaving(false);
    }
  }, [section]);

  const save = useCallback(async () => {
    setSaving(true);
    setError(null);
    try {
      await saveSection(section, draft);
    } catch (e) {
      console.error(e);
      setError(e.code === 'permission-denied'
        ? 'Nemaš prava za pisanje — provjeri firestore.rules i svoj UID.'
        : e.message || 'Greška pri spremanju.');
    } finally {
      setSaving(false);
    }
  }, [section, draft]);

  return { draft, setDraft, dirty, saving, error, save, saveValue, reset };
}
